import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PlaneLanding, Users, FileWarning, ExternalLink } from "lucide-react";

const VisitorLine = ({ v, extra }) => (
  <li className="flex items-start justify-between gap-2 text-sm py-1.5 border-b border-[#F4F4F1] last:border-0">
    <div className="min-w-0">
      <p className="font-medium text-[#1C1F1E] truncate">{v.visitor_name}</p>
      <p className="text-[11px] text-[#7A8A84]">
        {v.stay_from} → {v.stay_to} · {v.party_size} pax
      </p>
      {extra}
    </div>
    {v.request_id && (
      <Link to={`/requests/${v.request_id}`} className="text-[#1B4332] hover:text-[#40916C] shrink-0 mt-0.5"
            title="Open request" data-testid={`agency-summary-link-${v.booking_id}`}>
        <ExternalLink className="h-3.5 w-3.5" />
      </Link>
    )}
  </li>
);

export const AgencySummary = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/agency/summary").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const { upcoming_arrivals = [], current_visitors = [], pending_docs = [] } = data;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="agency-summary">
      <Card className="border-[#E8E8E6]" data-testid="agency-upcoming-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 uppercase tracking-wider text-[#7A8A84]">
            <PlaneLanding className="h-4 w-4 text-[#40916C]" /> Upcoming arrivals
            <Badge className="bg-[#F0F5F2] text-[#1B4332] border border-[#1B4332]/15 ml-auto">{upcoming_arrivals.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {upcoming_arrivals.length === 0 ? (
            <p className="text-xs text-[#7A8A84] py-2">No upcoming arrivals booked yet.</p>
          ) : (
            <ul>{upcoming_arrivals.map((v) => <VisitorLine key={v.booking_id} v={v} />)}</ul>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E8E8E6]" data-testid="agency-current-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 uppercase tracking-wider text-[#7A8A84]">
            <Users className="h-4 w-4 text-blue-600" /> Currently on site
            <Badge className="bg-blue-50 text-blue-800 border border-blue-200 ml-auto">{current_visitors.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {current_visitors.length === 0 ? (
            <p className="text-xs text-[#7A8A84] py-2">No visitors currently on site.</p>
          ) : (
            <ul>{current_visitors.map((v) => <VisitorLine key={v.booking_id} v={v} />)}</ul>
          )}
        </CardContent>
      </Card>

      <Card className={`border-[#E8E8E6] ${pending_docs.length ? "border-amber-300 bg-amber-50/30" : ""}`} data-testid="agency-docs-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 uppercase tracking-wider text-[#7A8A84]">
            <FileWarning className="h-4 w-4 text-amber-600" /> Documents needed
            <Badge className={`ml-auto border ${pending_docs.length ? "bg-amber-100 text-amber-900 border-amber-300" : "bg-[#F4F4F1] text-[#7A8A84] border-[#E8E8E6]"}`}>
              {pending_docs.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {pending_docs.length === 0 ? (
            <p className="text-xs text-[#7A8A84] py-2">All document packages are complete. 🎉</p>
          ) : (
            <ul>
              {pending_docs.map((v) => (
                <VisitorLine key={v.booking_id} v={v} extra={
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(v.missing || []).map((m) => (
                      <span key={m} className="text-[9px] bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-1.5 py-px">{m}</span>
                    ))}
                  </div>
                } />
              ))}
            </ul>
          )}
          {pending_docs.length > 0 && (
            <p className="text-[10px] text-amber-800 mt-2">Open the request and upload the missing files under Documents.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
